<ng-container *transloco="let convert;">
  <trap-focus [enableFocusTrap]="true" [enableArrowKeys]="true">
    <app-modal [modalId]="modalId()" class="bid_modal" [class.bid_modal--all-in]="showDripBreakdown">
      @if (isLoading) {
        <div class="loader-container">
          <app-spinner></app-spinner>
        </div>
      }
      @if (!isLoading && bidState === 'notBidding') {
        <header class="modal__header">
          <h1
            class="modal__title"
            id="bid-modal-title"
            aria-labelledby="bid-modal-title-inner"
            tabindex="-1"
            role="alert"
            >
            <span class="visually-hidden" id="bid-modal-title-inner">{{ convert('vehicles.bidModal.bidModalText') }}</span>
            <span aria-hidden="true">{{headerText}}</span>
          </h1>
        </header>
        @if (errorMessage) {
          <p class="text-danger fw-bold" role="alert" data-test-id="app-bid-modal-e-general-error-text" #error aria-live="assertive">{{ displayErrorMessage }}</p>
        }
        @if (showWarning) {
          <app-vehicle-changed-warning></app-vehicle-changed-warning>
        }
        <div class="modal__content" [class.modal__content--all-in]="showDripBreakdown">
          <!--
            The comp's vehicle identification block (Figma 10360:9141): the title in bold with the VIN on
            the line beneath it, sitting between the 'Bid' heading and the bid-type label.

            Gated on showDripBreakdown because flag-off tenants already name the vehicle -- their prose
            reads '... on this 2017 Toyota Camry XLE 4T1BF...' -- so rendering this as well would state it
            twice. M4.11's all-in copy dropped the vehicle and VIN from that sentence, which is why the
            comp lifts them out to here.

            The VIN keeps .stockwave-bid-buy-vin. Stockwave reads the VIN out of that hook and it exists
            only in the legacy prose, so with the flag on the modal currently exposes no VIN for the
            extension to find; this restores it.
          -->
          @if (showDripBreakdown) {
            <p class="bid_vehicle">
              <span class="bid_vehicle__title">{{ item.titleSimple }}</span>
              <span class="bid_vehicle__vin">{{ convert('common.textVin') }} <span class="stockwave-bid-buy-vin">{{ vin }}</span></span>
            </p>
          }
          @if (autoBidEnabled) {
            <!--
              The comp's section label for the radio pair (Figma 10503:25192). It reuses
              vehicles.bidModal.headerSelect -- the string the modal title renders with the flag off --
              rather than adding a key: with the breakdown showing, headerText returns labelBid ('Bid') to
              match the comp's 26px heading, which moves 'Select Bid Type' down to here.

              Gated on showDripBreakdown, like the block above and like the title change it pairs with.
              Flag off, the title still reads 'Select Bid Type', so this label would only repeat it, and
              the radios render exactly as they did before drip.

              role + aria-labelledby name the group for assistive tech instead of leaving the label as a
              visual heading only. They point at the label above, so they are bound only when it renders.
            -->
            @if (showDripBreakdown) {
              <p class="bid_types__heading mb-0" id="bm_bid_type_label">{{ convert('vehicles.bidModal.headerSelect') }}</p>
            }
            <div class="control-wrapper"
              [attr.role]="showDripBreakdown ? 'radiogroup' : null"
              [attr.aria-labelledby]="showDripBreakdown ? 'bm_bid_type_label' : null">
              <div class="custom-control custom-radio">
                <input type="radio"
                  id="bid"
                  name="bid-radios"
                  class="custom-control-input"
                  title="Bid"
                  data-test-id="app-bid-modal-e-bid-radio-button"
                  data-track-elem="radio"
                  data-track-trigger="filter"
                  data-track-name="Bid"
                  [attr.aria-label]="convert('vehicles.bidModal.labelBid')"
                  [disabled]="existingAutoBidAmount || !bidAllowed"
                  [checked]="isSingleBid()"
                  (change)="updateBidType(SINGLE)"
                  />
                <label class="custom-control-label bid_types__label" for="bid">{{ convert('vehicles.bidModal.labelBid') }}</label>
              </div>
              <div class="custom-control custom-radio">
                <input type="radio"
                  id="autoBid"
                  name="bid-radios"
                  class="custom-control-input"
                  [disabled]="existingAutoBidAmount || !bidAllowed"
                  title="Auto Bid"
                  data-test-id="app-bid-modal-e-autobid-radio-button"
                  data-track-elem="radio"
                  data-track-trigger="filter"
                  data-track-name="Auto-Bid"
                  aria-label="Auto Bid"
                  [checked]="!isSingleBid()"
                  (change)="updateBidType(AUTO)"
                  />
                <label class="custom-control-label bid_types__label" for="autoBid">{{ convert('vehicles.bidModal.labelAutoBid') }}</label>
              </div>
            </div>
          }
          @if (isSingleBid()) {
            <!--
              M4.7: with the flag on the breakdown goes ABOVE the prose, matching the comps and the
              auto-bid branch, which has always rendered its amount block first. The prose reads
              "you are agreeing to bid $X plus any applicable charges or fees", so the itemisation it
              refers to has to be on screen before it rather than below the fold.

              Two guarded outlets rather than one: flag off, today's bare stepper must stay exactly
              where it is -- BELOW the prose -- because moving it would be an unrelated layout change
              for every non-EHI tenant. Both branches still reach the SAME #AllInBreakdown, so the
              single-bid and auto-bid all-in treatments cannot diverge.
            -->
            @if (showDripBreakdown) {
              <ng-container *ngTemplateOutlet="AllInBreakdown"></ng-container>
            }
            <div class="modal__body" data-test-id="app-bid-modal-e-modal-bid-text">
              <!--
                M4.11: with the flag on, ONE paragraph replaces all THREE of today's blocks -- the
                "Important: By selecting Bid ..." sentence, the legacy transportation sentence, and the
                separate Selling Disclosures line. The comp (Figma 10360:9149) folds the disclosures
                acceptance into the binding sentence and quotes the breakdown's own TOTAL PURCHASE
                PRICE, so the figure the buyer is bound to is the figure itemised directly above it
                rather than a second number that appears nowhere in the breakdown.

                Flag off, all three blocks stay exactly as they are. This is an EHI/Canada copy change,
                not a rewrite of a disclosure every other tenant has already signed off.
              -->
              @if (showDripBreakdown) {
                <p id="bm_bid_binding_text" aria-labelledby="bid-modal-body-text">
                  <span [innerHTML]="convert('vehicles.bidModal.msgBindingBidAllInPartOne')"></span>
                  <!--
                    Split around the link rather than embedding an anchor in the translation string:
                    the href is config-driven (sellingDisclosureUrl) and routerLink cannot be applied
                    through [innerHTML]. Same PartOne/PartTwo shape initialOffer already uses.

                    No whitespace between the outlet and part two -- part two opens with '.', and a
                    newline there would render 'Selling Disclosures .'
                  -->
                  <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container><span
                    [innerHTML]="convert('vehicles.bidModal.msgBindingBidAllInPartTwo', { amount: bidQuotedPriceDisplay })"></span>
                </p>
                @if (showTransportationDisclosure) {
                  <p id="bm_bid_transportation_text"
                    [innerHTML]="convert('vehicles.bidModal.msgTransportationOptional')"></p>
                }
              } @else {
                <p aria-labelledby="bid-modal-body-text">
                  <strong>{{bidModalTranslations.textImportant}}</strong> {{bidModalTranslations.textBySelecting}}
                  <strong>{{bidModalTranslations.labelBid}}</strong>{{bidModalTranslations.textYouBid}}
                  {{bidAmountText}} {{bidModalTranslations.textAdditionalFees}} {{item.title}} <span class="stockwave-bid-buy-vin">{{vin}}</span> {{bidModalTranslations.textBindingAgreement}}
                </p>
                <p aria-labelledby="bid-modal-body-text">
                  {{bidModalTranslations.textTransportationService}}
                </p>
                <ng-container *ngTemplateOutlet="SellerDisclosures"></ng-container>
              }
              <ng-container *ngTemplateOutlet="financeSourceFeeMessage"></ng-container>
            </div>
            @if (!showDripBreakdown) {
              <ng-container *ngTemplateOutlet="BidAmount"></ng-container>
            }
            @if (isNotViewOnlyErrorMessage) {
              <p class="text-danger fw-bold" role="alert" data-test-id="app-bid-modal-e-bid-error-text" #error aria-live="polite">{{convert('error.' + errorMessage) }}</p>
            }
          } @else {
            <div class="modal__body" data-test-id="app-bid-modal-e-modal-autobid-text">
              <ng-container *ngTemplateOutlet="BidAmountOrBreakdown"></ng-container>
              <!--
                M4.11, exactly as the single-bid branch above. Comp: Figma 10360:9114.
                Auto Bid quotes TWO figures on purpose -- the bare maximum the buyer authorises, then
                the all-in total they would owe at that maximum. Both are in the comp; the bare one is
                the ceiling on the bidding, not the amount payable, which is why the total follows it.
              -->
              @if (showDripBreakdown) {
                <p id="bm_autobid_binding_text" aria-labelledby="bid-modal-body-text">
                  <span [innerHTML]="convert('vehicles.bidModal.msgBindingAutoBidAllInPartOne', { bidAmount: bidAmountText })"></span>
                  <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container><span
                    [innerHTML]="convert('vehicles.bidModal.msgBindingAutoBidAllInPartTwo', { amount: bidQuotedPriceDisplay })"></span>
                </p>
                @if (showTransportationDisclosure) {
                  <p id="bm_autobid_transportation_text"
                    [innerHTML]="convert('vehicles.bidModal.msgTransportationOptional')"></p>
                }
              } @else {
                <p aria-labelledby="bid-modal-body-text">
                  <strong>{{bidModalTranslations.textImportant}}</strong> {{bidModalTranslations.textBySelecting}}
                  {{bidModalTranslations.labelAutoBid}}{{bidModalTranslations.textBidUpTo}}
                  <strong>{{bidAmountText}}</strong> {{bidModalTranslations.textOnThe}}
                  {{item.title}} <span class="stockwave-bid-buy-vin">{{vin}}</span> {{bidModalTranslations.textBindingAgreement}}
                </p>
                <p aria-labelledby="bid-modal-body-text">
                  {{bidModalTranslations.textTransportationService}}
                </p>
                <ng-container *ngTemplateOutlet="SellerDisclosures"></ng-container>
              }
              <ng-container *ngTemplateOutlet="financeSourceFeeMessage"></ng-container>
            </div>
            @if (isNotViewOnlyErrorMessage) {
              <p class="text-danger fw-bold" role="alert" data-test-id="app-bid-modal-e-autobid-error-text" #error aria-live="polite">{{convert('error.' + errorMessage) }}</p>
            }
          }
          <div class="d-flex flex-column flex-lg-row lg-justify-content-start modal_actions pb-3" [class.mt-0]="showDripBreakdown">
            <button
              allytm-async
              id="bm_bid_button"
              class="btn btn-primary stockwave-bid-button"
              type="submit"
              (click)="bid()"
              [attr.allytmln]="getBidButtonAnalyticsTag()"
              data-track-elem="button"
              data-track-name="Bid"
              data-test-id="app-bid-modal-e-bid-button"
              >
              {{ item.autoBidStatus | autoBidButtonText }}
            </button>
            <button
              id="bm_cancel_button"
              class="ms-lg-2 mt-3 mt-lg-0 btn btn-link"
              type="button"
              data-test-id="app-bid-modal-e-cancel-button"
              (click)="close()"
              data-track-elem="button"
              data-track-name="Cancel"
              data-track-trigger="close"
              >
              <span>{{ convert('common.btnCancel') }}</span>
            </button>
          </div>
        </div>
        <div class="modal-close-container">
          <button
            id="bm_close_x_button"
            class="modal__close btn-link"
            [attr.aria-label]="convert('common.modalClose')"
            (click)="close()"
            data-track-elem="icon"
            data-track-name="close"
            data-track-trigger="close"
            data-test-id="app-bid-modal-e-close-modal-button"
            >
            <i class="allycon-glyph-close text-primary" aria-hidden="true"></i>
          </button>
        </div>
      }
      @if (bidState === 'bidding') {
        <app-action-submitting actionText="{{getActionText()}}"></app-action-submitting>
      }
      @if (bidState === 'biddingComplete') {
        <app-action-submitted
          (close)="close()"
          actionText="{{getSuccessText()}}"
          [tenantName]="tenantName()"
        ></app-action-submitted>
      }
    </app-modal>
  </trap-focus>

  <ng-template #financeSourceFeeMessage>
    @if (isFinanceSourceFeeIncluded()) {
      <p aria-labelledby="bid-modal-finance-source-fee-text" [innerHTML]="convert('vehicles.bidModal.textFinanceSourceFee',{name: financeSource.financeSourceName, amount: financeSource.feeAmount})"></p>
    }
  </ng-template>

  <!--
    Today's stepper block: its own label above the control. Used whenever the all-in breakdown is not
    rendering, which is every flag-off case.
  -->
  <ng-template #BidAmount>
    <div class="col-7 p-0">
      <div class="form-group m-0">
        <label for="inc-stepper" id="inc-stepper-label">
          <span aria-hidden="true">{{getBidAmountText()}}</span>
        </label>

        <ng-container *ngTemplateOutlet="BidStepper; context: { labelledBy: 'inc-stepper-label' }"></ng-container>
      </div>
    </div>
  </ng-template>

  <!--
    The control itself, with no label of its own, so the two hosts above and below can each supply the
    labelling their layout needs. Extracted rather than copied: it is the only place the buyer names a
    binding amount, and two copies drifting -- a mask, a separator, a step -- is a money defect.

    `labelledBy` is the id of whichever element names it: the <label> in #BidAmount, or the breakdown's
    own base-amount label when the control is projected into it.
  -->
  <ng-template #BidStepper let-labelledBy="labelledBy">
    <div class="control-wrapper">
      <div class="input-group btn-group" role="group" [attr.aria-label]="getBidAmountText()">
        <span class="input-group-prepend">
          <button
            [attr.aria-label]="setAriaLabel"
            [attr.aria-disabled]="bidAmount <= item.currentAmount"
            class="btn btn-outline-default prepend"
            type="button"
            (click)="decrement()"
            data-track-elem="button"
            data-track-name="Decrement Bid"
            data-test-id="app-bid-modal-e-decrement-bid-button"
            id="bm_decrease_bid_button"
            >
            <img src="assets/icons/minus.svg" alt="decrement bid" class="icon-minus">
          </button>
        </span>
        @if (!frenchCanadian) {
          <input
            aria-live="polite"
            aria-atomic="true"
            id="bm_bid_amount_textbox"
            class="form-control stockwave-bid-buy-amount"
            name="inc-stepper"
            prefix="$"
            mask="separator.0"
            thousandSeparator=","
            separatorLimit="100000"
            [attr.aria-labelledby]="labelledBy"
            [attr.aria-label]="bidAmount"
            [ngModel]="bidAmount"
            (ngModelChange)="bidAmount = $event"
            />
        }
        @if (frenchCanadian) {
          <input
            aria-live="polite"
            aria-atomic="true"
            id="bm_bid_amount_textbox"
            class="form-control canadian-input stockwave-bid-buy-amount"
            name="inc-stepper"
            mask="separator.0"
            thousandSeparator=" "
            separatorLimit="100000"
            [attr.aria-labelledby]="labelledBy"
            [attr.aria-label]="bidAmount"
            [ngModel]="bidAmount"
            (ngModelChange)="bidAmount = $event"
            />
        }
        <span class="input-group-append">
          <button
            [attr.aria-label]="convert('vehicles.bidModal.ariaLabelAmountIncrease')"
            id="bm_increase_bid_button"
            class="btn btn-outline-default append"
            type="button"
            data-test-id="app-bid-modal-e-increment-bid-button"
            (click)="increment()"
            data-track-elem="button"
            data-track-name="Increment Bid"
            >
            <img src="assets/icons/plus.svg" alt="increment bid" class="icon-plus">
          </button>
        </span>
      </div>
    </div>
  </ng-template>

  <!--
    All-in pricing (M4.3), Pattern B. The stepper moves inside the breakdown, labelled by the
    breakdown's own base-amount label, with the required fees and optional transportation itemised
    beneath it and the total updating as the buyer works the control.

    The projected wrapper must carry appModalPriceBreakdownAmount: content matching no ng-content
    selector is discarded silently rather than rendered, so the stepper would simply vanish without it.
  -->
  <ng-template #AllInBreakdown>
    <app-modal-price-breakdown
      idPrefix="bm_price"
      [pattern]="additivePattern"
      [baseAmount]="dripBidBaseAmount"
      [feeRows]="dripFeeRows"
      [transportationFee]="dripTransportationFee"
      [translations]="dripBreakdownTranslations"
      [showCents]="false"
      [useProjectedAmount]="true"
      [inlineControlOnNarrow]="true">
      <span appModalPriceBreakdownAmount>
        <ng-container *ngTemplateOutlet="BidStepper; context: { labelledBy: 'bm_price_base_label' }"></ng-container>
      </span>
    </app-modal-price-breakdown>
  </ng-template>

  <!--
    One outlet for both the single-bid and auto-bid branches, so the two cannot end up with different
    all-in treatments.
  -->
  <ng-template #BidAmountOrBreakdown>
    @if (showDripBreakdown) {
      <ng-container *ngTemplateOutlet="AllInBreakdown"></ng-container>
    } @else {
      <ng-container *ngTemplateOutlet="BidAmount"></ng-container>
    }
  </ng-template>


  <!--
    The Selling Disclosures link, extracted in M4.11 so the legacy paragraph and the new binding
    sentence share ONE definition of it instead of two that can drift apart.

    The @else branch is not padding. showSellingDisclosure gates the disclosuresLinkEnabled flag, and
    with that flag off the binding sentence still has to read "... and you accept the Selling
    Disclosures." -- the words stay, only the anchor goes. Without it part one would end mid-phrase on
    "the". The flag is named for the LINK, so dropping the link and keeping the noun is what it asks for.
  -->
  <ng-template #SellingDisclosuresLink>
    @if (showSellingDisclosure) {
      <strong><a
        [attr.aria-label]="convert('vehicles.bidModal.ariaLabelSellingDisclosures')"
        target="_blank"
        rel="opener"
        routerLink="{{ sellingDisclosureUrl }}"
        data-track-elem="button"
        data-track-name="Selling Disclosures"
        data-test-id="app-bid-modal-e-selling-disclosures-link"
        >{{ convert('vehicles.bidModal.textSellingDisclosures') }}</a></strong>
    } @else {
      <strong>{{ convert('vehicles.bidModal.textSellingDisclosures') }}</strong>
    }
  </ng-template>

  <ng-template #SellerDisclosures>
    @if (showSellingDisclosure) {
      <p aria-labelledby="disclosure-text">
        <span class="visually-hidden" id="disclosure-text"> {{ convert('vehicles.bidModal.textDisclosures') }} </span>
        <span aria-hidden="true"> {{ convert('vehicles.bidModal.textSelectingBid') }} </span>
        <ng-container *ngTemplateOutlet="SellingDisclosuresLink"></ng-container>.
      </p>
    }
  </ng-template>
    </ng-container>
